import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { itemMatchedOnLabel } from '@supply-mover-2-0/core';
import type { ItemMatchedOnModel } from '@supply-mover-2-0/shared';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Tooltip } from '../overlays/Tooltip';

export interface ItemMatchedOnInfoProps {
	matchedOn: Array<ItemMatchedOnModel> | null | undefined;
	/** Staff screens name whose number it is; the storefront does not. */
	audience?: 'staff' | 'storefront';
	className?: string;
}

/**
 * PD-17503 — a small info icon beside an item search result's name, shown
 * only when the result matched on a name other than the words it shows
 * ("Limbach's item number", "galvanized = GALV", "Extended description"),
 * with the reasons in its tooltip. Ordinary results carry nothing; the
 * reasons are spelled out only on matching screens (Add from a list, the
 * quote request review).
 */
export function ItemMatchedOnInfo({ matchedOn, audience = 'staff', className }: ItemMatchedOnInfoProps): React.ReactElement | null {
	const { t } = useTranslation();
	const colors = useThemeColors();
	if (matchedOn == null || matchedOn.length === 0) {
		return null;
	}
	const lines = matchedOn.map((label) => {
		const { text, detail } = itemMatchedOnLabel(label, t, audience);
		return detail == null ? text : `${text}: ${t(detail)}`;
	});
	return (
		<Tooltip
			content={
				<div className="flex flex-col gap-0.5 text-left">
					<span className="font-semibold">{t('Matched on')}</span>
					{lines.map((line, index) => (
						<span key={index}>{line}</span>
					))}
				</div>
			}
		>
			<span
				role="img"
				aria-label={t('Matched on: {{reasons}}', { reasons: lines.join('; ') })}
				className={`inline-flex shrink-0 items-center ${colors.classes.textSecondary} ${className ?? ''}`}
			>
				<FontAwesomeIcon icon={faCircleInfo} className="text-xs" />
			</span>
		</Tooltip>
	);
}
